import React, { useReducer, useState, useEffect } from "react";
import { safeMutate } from "../../../utils/GeneralUtils";
import { widgetActionSection, widgetActionPosition } from "../../../components/generic/Actions/constants";
import { widgetActionTypeComponentMapper } from "../../../components/generic/Actions/WidgetActions/utils";
import { map } from "../../../utils/CoreExtensions/ObjectExtensions";
import { eventTracker, getEventDetails } from "../../../components/generic/Utils/eventTracker";


/*
initialState = {
  <actionUId>: {
    actionType: <actionType>,
    pinned: <true | false>,
    section: <header_action | footer_action>,
    data: <Hash for action component's data>,
    props: <Hash for action component's properties>
  }
}
configBuilder = <BuilderMethod which will build the actionConfig on first time and each submit>
*/

const useWidgetActionsBuilder = (initialState, configBuilder, additionalOptions = {}) => {

  // widget actions' state maintainance and updation
  const reducer = (state, action) => {
    if (action.uId) {
      const uId = action.uId;
      return safeMutate(state, newState => {
        newState[uId].data = action.data;
        return newState;
      });
    } else if (action.reset) {
      return action.prevState;
    }
  };
  const initialWidgetConfig = safeMutate(configBuilder(initialState), draftWidgetConfig => {
    if(draftWidgetConfig.remoteOptions)
      draftWidgetConfig.remoteOptions.fetchCondition = () => !(additionalOptions.hasGlobalFilters)
  });
  const [lastSubmittedState, setLastSubmittedState] = useState(initialState);
  const [widgetConfig, setWidgetConfig] = useState(initialWidgetConfig);
  const [state, dispatch] = useReducer(reducer, initialState);

  const mergeGlobalFilters = (event) => {
    const globalFiltersPayload = event.detail;
    if (widgetConfig.remoteOptions?.remoteOptions) {
      setWidgetConfig(prevWidgetConfig => safeMutate(prevWidgetConfig, draftWidgetConfig => {
        draftWidgetConfig.remoteOptions.fetchCondition = () => true;
        draftWidgetConfig.remoteOptions.remoteOptions.urlOptions ||= {};
        draftWidgetConfig.remoteOptions.remoteOptions.urlOptions.filters = {
          ...draftWidgetConfig.remoteOptions.remoteOptions.urlOptions.filters,
          ...globalFiltersPayload
        };
      }));
    }
  };

  useEffect(() => {
    document.addEventListener(CustomEs6EventsConfig.event.WIDGET_GLOBAL_FILTERS_DISPATCH, mergeGlobalFilters, false);
    return () => {
      document.removeEventListener(CustomEs6EventsConfig.event.WIDGET_GLOBAL_FILTERS_DISPATCH, mergeGlobalFilters, false);
    };
  }, []);

  const onSubmitHandler = (_event, _uId) => {
    setLastSubmittedState(state);
    setWidgetConfig(configBuilder(state));
    const eventDetails = additionalOptions?.eventDetailsBuilder?.(state);
    const { eventName, payload } = getEventDetails(eventDetails);
    eventTracker(eventName, payload);
  };

  const onCancelHandler = (_event, _uId) => {
    dispatch({ reset: true, prevState: lastSubmittedState });
  };

  const actionsTemplateBasedOnPinStatus = () => structuredClone({ pinned: [], notPinned: [] })
  const actionsTemplate = () => structuredClone({ left: actionsTemplateBasedOnPinStatus(), right: actionsTemplateBasedOnPinStatus() });
  let actions = {
    headerActions: actionsTemplate(),
    footerActions: actionsTemplate()
  };
  const widgetUtils = { onSubmitHandler, setWidgetConfig }

  map(state, ([uId, actionAttributes]) => {
    const actionSection = (actionAttributes.section === widgetActionSection.HEADER_ACTION) ? "headerActions" : "footerActions";
    const actionPosition = actionAttributes.position || widgetActionPosition.RIGHT;
    const pinnedStatusSection = actionAttributes.pinned ? "pinned" : "notPinned";
    const Component = widgetActionTypeComponentMapper[actionAttributes.actionType];
    const actionData = {
      ...actionAttributes.data,
      ...(widgetConfig.actions || {})[uId]
    };

    actions[actionSection][actionPosition][pinnedStatusSection].push(
      <Component
        { ...(actionAttributes.props || {}) }
        key={ uId }
        data={ actionData }
        lastSubmittedData={ lastSubmittedState[uId].data }
        uId={ uId }
        tooltip={ actionAttributes.tooltip }
        dispatcher={ dispatch }
        onSubmit={ onSubmitHandler }
        onCancel={ onCancelHandler }
        additionalOptions={ additionalOptions }
      />
    );
  });

  return { state, widgetConfig, actions, widgetUtils };
};

export default useWidgetActionsBuilder;
